import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Divider,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CorrectIcon,
  Cancel as WrongIcon,
  Edit as OverrideIcon,
  Save as SaveIcon,
  Info as AIIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';

interface AttemptSummary {
  id: number;
  student_id: number;
  student_name: string;
  attempt_number: number;
  status: string;
  total_score: number | null;
  total_possible: number | null;
  percentage: number | null;
  submitted_at: string | null;
  graded_at: string | null;
}

interface ResponseDetail {
  id: number;
  question_id: number;
  question_type: string;
  question_text: string;
  correct_answer: string | null;
  student_answer: string | null;
  selected_option: string | null;
  is_correct: boolean | null;
  points_earned: number | null;
  points_possible: number | null;
  ai_suggested_score: number | null;
  ai_feedback: string | null;
  teacher_override_score: number | null;
  teacher_feedback: string | null;
  grading_method: string | null;
}

interface AttemptDetail {
  id: number;
  student_name: string;
  attempt_number: number;
  status: string;
  total_score: number | null;
  total_possible: number | null;
  percentage: number | null;
  responses: ResponseDetail[];
}

interface QuizGradingDialogProps {
  open: boolean;
  onClose: () => void;
  quizAssignmentId: number | null;
  quizTitle: string;
}

const QuizGradingDialog: React.FC<QuizGradingDialogProps> = ({
  open,
  onClose,
  quizAssignmentId,
  quizTitle,
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { isMobile } = useIsMobile();

  const [attempts, setAttempts] = useState<AttemptSummary[]>([]);
  const [selectedAttempt, setSelectedAttempt] = useState<AttemptDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Override editing
  const [overrideId, setOverrideId] = useState<number | null>(null);
  const [overrideScore, setOverrideScore] = useState('');
  const [overrideFeedback, setOverrideFeedback] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);

  useEffect(() => {
    if (open && quizAssignmentId && token) {
      fetchAttempts();
      setSelectedAttempt(null);
    }
  }, [open, quizAssignmentId, token]);

  const fetchAttempts = async () => {
    if (!quizAssignmentId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`/api/quiz-assignments/${quizAssignmentId}/attempts`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (data.success) {
        setAttempts(data.attempts || []);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to load attempts');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttemptDetail = async (attemptId: number) => {
    if (!token) return;
    setLoadingDetail(true);
    try {
      const resp = await fetch(`/api/quiz-attempts/${attemptId}/responses`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (data.success) {
        setSelectedAttempt(data.attempt);
      }
    } catch (err) {
      setError('Failed to load attempt detail');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleOverrideSave = async () => {
    if (!overrideId || !token) return;
    setSavingOverride(true);
    try {
      const resp = await fetch(`/api/quiz-responses/${overrideId}/override`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          score: parseFloat(overrideScore),
          feedback: overrideFeedback || null,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        // Refresh detail
        if (selectedAttempt) {
          fetchAttemptDetail(selectedAttempt.id);
        }
        fetchAttempts();
        setOverrideId(null);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to save override');
    } finally {
      setSavingOverride(false);
    }
  };

  const handleReviewAttempt = async (attemptId: number) => {
    if (!token) return;
    try {
      const resp = await fetch(`/api/quiz-attempts/${attemptId}/review`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (data.success) {
        fetchAttempts();
        if (selectedAttempt?.id === attemptId) {
          setSelectedAttempt({ ...selectedAttempt, status: 'reviewed' });
        }
      } else {
        setError(data.error);
      }
    } catch {
      setError('Failed to accept grade');
    }
  };

  const handleReturnAttempt = async (attemptId: number) => {
    if (!token) return;
    try {
      const resp = await fetch(`/api/quiz-attempts/${attemptId}/return`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (data.success) {
        fetchAttempts();
        if (selectedAttempt?.id === attemptId) {
          setSelectedAttempt({ ...selectedAttempt, status: 'returned' });
        }
      } else {
        setError(data.error);
      }
    } catch {
      setError('Failed to return attempt');
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'graded': return t('quiz.status.pendingReview', 'Pending Review');
      case 'reviewed': return t('quiz.status.reviewed', 'Accepted');
      case 'returned': return t('quiz.status.returned', 'Returned');
      case 'in_progress': return t('quiz.status.inProgress', 'In Progress');
      default: return status;
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'graded': return 'warning';
      case 'reviewed': return 'success';
      case 'returned': return 'error';
      case 'in_progress': return 'primary';
      default: return 'default';
    }
  };

  const handleClose = () => {
    setSelectedAttempt(null);
    setOverrideId(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="quiz-grading-dialog-title"
    >
      <DialogTitle
        id="quiz-grading-dialog-title"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}
      >
        <Box>
          <Typography variant="h6">{t('quiz.grading.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{quizTitle}</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label={t('common.close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        {!selectedAttempt ? (
          /* Student attempts list */
          loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : attempts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">{t('quiz.grading.noAttempts')}</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('quiz.grading.student')}</TableCell>
                    <TableCell>{t('quiz.grading.attempt')}</TableCell>
                    <TableCell>{t('quiz.grading.status')}</TableCell>
                    <TableCell>{t('quiz.grading.score')}</TableCell>
                    <TableCell>{t('quiz.grading.submitted')}</TableCell>
                    <TableCell>{t('quiz.grading.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attempts.map((a) => (
                    <TableRow key={a.id} hover>
                      <TableCell>{a.student_name}</TableCell>
                      <TableCell>#{a.attempt_number}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(a.status)}
                          size="small"
                          color={getStatusColor(a.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {a.total_score !== null
                          ? `${a.total_score} / ${a.total_possible} (${a.percentage}%)`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => fetchAttemptDetail(a.id)}
                            aria-label={t('quiz.grading.reviewAttempt')}
                          >
                            {t('quiz.grading.review')}
                          </Button>
                          {a.status === 'graded' && (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => handleReviewAttempt(a.id)}
                              aria-label={t('quiz.grading.acceptGrade', 'Accept Grade')}
                            >
                              {t('quiz.grading.acceptGrade', 'Accept')}
                            </Button>
                          )}
                          {(a.status === 'graded' || a.status === 'reviewed') && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => handleReturnAttempt(a.id)}
                              aria-label={t('quiz.grading.returnToStudent', 'Return to Student')}
                            >
                              {t('quiz.grading.returnToStudent', 'Return')}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : (
          /* Detailed response view */
          <Box sx={{ p: 2 }}>
            <Button
              startIcon={<BackIcon />}
              onClick={() => setSelectedAttempt(null)}
              sx={{ mb: 2 }}
              aria-label={t('common.back')}
            >
              {t('common.back')}
            </Button>

            {loadingDetail ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {selectedAttempt.student_name} — {t('quiz.grading.attempt')} #{selectedAttempt.attempt_number}
                      </Typography>
                      <Typography variant="body2">
                        {t('quiz.grading.score')}: {selectedAttempt.total_score} / {selectedAttempt.total_possible}
                        {selectedAttempt.percentage !== null && ` (${selectedAttempt.percentage}%)`}
                      </Typography>
                      <Chip
                        label={getStatusLabel(selectedAttempt.status)}
                        size="small"
                        color={getStatusColor(selectedAttempt.status)}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {selectedAttempt.status === 'graded' && (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleReviewAttempt(selectedAttempt.id)}
                          aria-label={t('quiz.grading.acceptGrade', 'Accept Grade')}
                        >
                          {t('quiz.grading.acceptGrade', 'Accept Grade')}
                        </Button>
                      )}
                      {(selectedAttempt.status === 'graded' || selectedAttempt.status === 'reviewed') && (
                        <Button
                          variant="outlined"
                          color="warning"
                          size="small"
                          onClick={() => handleReturnAttempt(selectedAttempt.id)}
                          aria-label={t('quiz.grading.returnToStudent', 'Return to Student')}
                        >
                          {t('quiz.grading.returnToStudent', 'Return to Student')}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Paper>

                {selectedAttempt.responses.map((resp, idx) => {
                  const isOverriding = overrideId === resp.id;
                  const needsReview = resp.grading_method === 'ai' && resp.teacher_override_score === null;

                  return (
                    <Paper
                      key={resp.id}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        borderLeft: `4px solid ${resp.is_correct ? '#4caf50' : '#f44336'}`,
                        bgcolor: needsReview ? '#fff8e1' : '#fff',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">Q{idx + 1}: {resp.question_text}</Typography>
                        <Chip
                          label={`${resp.points_earned ?? 0} / ${resp.points_possible}`}
                          size="small"
                          color={resp.is_correct ? 'success' : 'error'}
                        />
                      </Box>

                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>{t('quiz.results.yourAnswer')}:</strong>{' '}
                        {resp.selected_option || resp.student_answer || t('quiz.results.noAnswer')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>{t('quiz.correctAnswer')}:</strong> {resp.correct_answer}
                      </Typography>

                      {resp.grading_method === 'ai' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                          <AIIcon sx={{ fontSize: 16, color: '#e65100' }} />
                          <Typography variant="caption" color="#e65100">
                            {t('quiz.grading.aiGraded')}: {resp.ai_suggested_score} pts — {resp.ai_feedback}
                          </Typography>
                        </Box>
                      )}

                      {needsReview && !isOverriding && (
                        <Chip
                          label={t('quiz.grading.needsReview')}
                          size="small"
                          sx={{ mt: 1, bgcolor: '#fff3e0', color: '#e65100' }}
                        />
                      )}

                      {isOverriding ? (
                        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                          <TextField
                            label={t('quiz.grading.overrideScore')}
                            type="number"
                            value={overrideScore}
                            onChange={(e) => setOverrideScore(e.target.value)}
                            inputProps={{ min: 0, max: resp.points_possible, step: 0.5 }}
                            size="small"
                            sx={{ width: 100 }}
                            aria-label={t('quiz.grading.overrideScore')}
                          />
                          <TextField
                            label={t('quiz.grading.feedback')}
                            value={overrideFeedback}
                            onChange={(e) => setOverrideFeedback(e.target.value)}
                            size="small"
                            sx={{ flex: 1 }}
                            aria-label={t('quiz.grading.feedback')}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={savingOverride ? <CircularProgress size={14} /> : <SaveIcon />}
                            onClick={handleOverrideSave}
                            disabled={savingOverride}
                            aria-label={t('common.save')}
                          >
                            {t('common.save')}
                          </Button>
                          <Button size="small" onClick={() => setOverrideId(null)} aria-label={t('common.cancel')}>
                            {t('common.cancel')}
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          size="small"
                          startIcon={<OverrideIcon />}
                          onClick={() => {
                            setOverrideId(resp.id);
                            setOverrideScore(String(resp.points_earned ?? 0));
                            setOverrideFeedback(resp.teacher_feedback || '');
                          }}
                          sx={{ mt: 1 }}
                          aria-label={t('quiz.grading.override')}
                        >
                          {t('quiz.grading.override')}
                        </Button>
                      )}
                    </Paper>
                  );
                })}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={handleClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuizGradingDialog;
